const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '19 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const doctrineQuestionPerspectives = [
    {
        slug: 'is-the-rapture-biblical-and-when-does-it-happen',
        category: 'Death and Final Things',
        question: 'Is the rapture biblical, and when does it happen?',
        shortTitle: 'Is the rapture biblical?',
        summary: 'Christians broadly affirm that living believers will be gathered to Christ at his return, as 1 Thessalonians 4 describes. The dispute is whether this gathering occurs before a distinct tribulation, after persecution but before God’s climactic wrath, or as part of the single public return of Christ after the tribulation.',
        introduction: 'The English word “rapture” comes from the Latin rendering of Paul’s verb for being “caught up”; disagreement concerns its timing and relationship to the visible second coming, not whether believers will meet the Lord. Interpretations depend on how Daniel, the Olivet Discourse, 1–2 Thessalonians, and Revelation are coordinated and on whether Israel and the church have distinct prophetic roles.',
        seoDescription: 'Compare pretribulation, prewrath, and posttribulation views of the rapture and its relationship to Christ’s second coming.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Matthew 24:15–31', href: '/read/Matt.24/#Matt.24.15-Matt.24.31' },
            { label: '1 Corinthians 15:50–58', href: '/read/1Cor.15/#1Cor.15.50-1Cor.15.58' },
            { label: '1 Thessalonians 4:13–18', href: '/read/1Thess.4/#1Thess.4.13-1Thess.4.18' },
            { label: '2 Thessalonians 2:1–12', href: '/read/2Thess.2/#2Thess.2.1-2Thess.2.12' },
        ],
        readings: [
            {
                id: 'pretribulation-rapture',
                title: 'Christ gathers the church before the tribulation',
                thesis: 'Christ gathers the church before the final tribulation and later returns visibly with his saints to establish his kingdom.',
                accent: 'navy',
                badges: ['Dispensational', 'Premillennial', 'Pentecostal', 'Conservative'],
                summary: 'This view distinguishes Christ’s imminent coming for the church from his public descent in judgement at the end of the tribulation. It commonly understands the final seven-year period as the seventieth week of Daniel, with God resuming a distinctive programme for Israel after removing the church from the coming wrath.',
                contribution: 'It strongly preserves imminence, the church’s hope of deliverance from divine wrath, and a straightforward distinction between Israel and the church in prophetic interpretation.',
                caution: 'This reading must demonstrate that the New Testament separates the gathering and visible return into two stages and explain passages that place the gathering after identifiable tribulation events.',
                supporters: [
                    { name: 'Craig A. Blaising', sourceId: 'zondervan-rapture' },
                    { name: 'Assemblies of God (USA)', sourceId: 'ag-rapture' },
                ],
                predicates: [
                    {
                        label: 'Israel and the church retain distinguishable prophetic roles',
                        topicSlug: 'are-israel-and-the-church-distinct-in-gods-plan',
                        readingId: 'distinct-peoples',
                        linked: true,
                    },
                    {
                        label: 'God still has a distinct future purpose for ethnic Israel',
                        topicSlug: 'does-god-still-have-a-future-for-israel',
                        readingId: 'distinct-future',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'prewrath-rapture', linked: true },
                    { type: 'contrasts with', target: 'posttribulation-gathering', linked: true },
                ],
            },
            {
                id: 'prewrath-rapture',
                title: 'Christ gathers the church before final wrath',
                thesis: 'The church endures Antichrist’s persecution but is gathered before God pours out the final Day-of-the-Lord wrath.',
                accent: 'amber',
                badges: ['Prewrath', 'Premillennial', 'Evangelical', 'Moderate'],
                summary: 'The prewrath position distinguishes human and satanic persecution from God’s eschatological wrath. It therefore expects the church to witness the great tribulation and the revealing of the lawless one, followed by unmistakable cosmic signs, the gathering of believers, and then divine judgement. The rapture is near the end of the final crisis but before its climactic wrath.',
                contribution: 'It integrates the church’s promised deliverance from wrath with New Testament preparation for severe persecution and gives a sequential reading to the signs described in Matthew 24, 2 Thessalonians 2, and Revelation.',
                caution: 'The view must establish its distinction between tribulation and wrath within disputed apocalyptic sequences and explain why the proposed timing is not stated more directly in the epistles.',
                supporters: [{ name: 'Alan D. Hultberg', sourceId: 'zondervan-rapture' }],
                predicates: [
                    {
                        label: 'The church may endure persecution while being spared God’s final punitive wrath',
                        topicSlug: 'is-hell-eternal',
                        readingId: 'eternal-conscious-punishment',
                        linked: true,
                    },
                    {
                        label: 'Israel’s future can remain significant without requiring the church’s earlier removal',
                        topicSlug: 'does-god-still-have-a-future-for-israel',
                        readingId: 'distinct-future',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'pretribulation-rapture', linked: true },
                    { type: 'qualifies', target: 'posttribulation-gathering', linked: true },
                ],
            },
            {
                id: 'posttribulation-gathering',
                title: 'Christ gathers believers after the tribulation',
                thesis: 'The rapture is the church’s gathering to welcome Christ during his single visible return after the tribulation.',
                accent: 'teal',
                badges: ['Posttribulation', 'Historic premillennial', 'Amillennial affinity', 'Ecumenical'],
                summary: 'This view reads 1 Thessalonians 4 as a royal-arrival scene: believers rise to meet the descending Lord and accompany him in triumph. The gathering, resurrection, judgement, and public appearing belong to one complex event rather than a secret removal years earlier. Christians should therefore expect endurance through tribulation while hoping in resurrection at Christ’s arrival.',
                contribution: 'It gives a direct reading to texts that associate the gathering with a loud command, trumpet, resurrection, and public coming, and it aligns the church’s hope with Jesus’ gathering of the elect after tribulation in Matthew 24.',
                caution: 'This reading must explain promises of deliverance from wrath and how imminent expectation functions if identifiable tribulation events precede Christ’s return.',
                supporters: [{ name: 'Douglas J. Moo', sourceId: 'zondervan-rapture' }],
                predicates: [
                    {
                        label: 'The church is the renewed covenant people gathered in Christ',
                        topicSlug: 'are-israel-and-the-church-distinct-in-gods-plan',
                        readingId: 'one-renewed-people',
                        linked: true,
                    },
                    {
                        label: 'Christian hope culminates in bodily resurrection and renewed creation',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'pretribulation-rapture', linked: true },
                    { type: 'contrasts with', target: 'prewrath-rapture', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'zondervan-rapture',
                'Craig A. Blaising, Alan D. Hultberg, and Douglas J. Moo',
                'Three Views on the Rapture: Pretribulation, Prewrath, or Posttribulation',
                'A Zondervan Academic comparison of the three principal evangelical timings of the rapture.',
                'https://zondervanacademic.com/products/three-views-on-the-rapture1',
            ),
            source(
                'ag-rapture',
                'Assemblies of God (USA)',
                'Rapture of the Church',
                'An official Pentecostal defence of an imminent gathering of the church before the tribulation.',
                'https://ag.org/Beliefs/Position-Papers/Rapture-of-the-Church',
            ),
        ],
        generation,
    },
    {
        slug: 'where-was-jesus-between-death-and-resurrection',
        category: 'Christ and Salvation',
        question: 'Where was Jesus between death and resurrection?',
        shortTitle: 'Jesus between death and resurrection',
        summary: 'Christians agree that Jesus truly died, was buried, and rose bodily on the third day. They differ over how to describe his human soul during Holy Saturday: as descending victoriously to the realm of the dead, as proclaiming triumph particularly to imprisoned spirits, or as remaining under the state and power of death without a separate saving mission to hell.',
        introduction: 'The question draws on the creed’s phrase “he descended into hell”, Jesus’ promise to the thief, Peter’s use of Psalm 16, and difficult passages in Ephesians and 1 Peter. Much disagreement comes from the changing meanings of “hell”: the realm of the dead, the place of final punishment, or the full experience of death and burial.',
        seoDescription: 'Compare Christian interpretations of where Jesus was on Holy Saturday between his crucifixion and bodily resurrection.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Luke 23:39–49', href: '/read/Luke.23/#Luke.23.39-Luke.23.49' },
            { label: 'Acts 2:22–36', href: '/read/Acts.2/#Acts.2.22-Acts.2.36' },
            { label: 'Ephesians 4:7–10', href: '/read/Eph.4/#Eph.4.7-Eph.4.10' },
            { label: '1 Peter 3:18–22', href: '/read/1Pet.3/#1Pet.3.18-1Pet.3.22' },
        ],
        readings: [
            {
                id: 'victorious-descent',
                title: 'Christ proclaimed victory to the dead',
                thesis: 'Christ’s human soul entered the realm of the dead, proclaimed his victory, and liberated the righteous who awaited redemption.',
                accent: 'teal',
                badges: ['Catholic', 'Orthodox', 'Patristic', 'Traditional'],
                summary: 'This reading distinguishes Hades or Sheol from the final hell of the damned. Jesus shares the human condition of death yet enters it as the victorious Saviour. Eastern Christian worship portrays him shattering Hades and raising Adam and Eve; Catholic teaching emphasises the completion of his messianic mission and the opening of heaven to the righteous who died before him.',
                contribution: 'It gives theological content to Holy Saturday, connects the resurrection with Christ’s conquest of death, and preserves a long-standing creedal and liturgical interpretation across Eastern and Western churches.',
                caution: 'This view must distinguish biblical teaching from later imagery and explain precisely whom Christ addressed or liberated without suggesting a second saving work beyond the cross.',
                supporters: [
                    { name: 'Catechism of the Catholic Church', sourceId: 'catechism-descent' },
                    { name: 'Orthodox Church in America', sourceId: 'oca-holy-saturday' },
                ],
                predicates: [
                    {
                        label: 'The dead remain personally existent while awaiting resurrection',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'conscious-intermediate-state',
                        linked: true,
                    },
                    {
                        label: 'Those without an earthly opportunity may encounter Christ beyond death',
                        topicSlug: 'what-happens-to-people-who-never-hear-the-gospel',
                        readingId: 'postmortem-opportunity',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'proclamation-to-spirits', linked: true },
                    { type: 'contrasts with', target: 'state-of-death', linked: true },
                ],
            },
            {
                id: 'proclamation-to-spirits',
                title: 'Christ announced victory to imprisoned spirits',
                thesis: 'The risen or vivified Christ announced his victory to imprisoned spiritual powers rather than offering salvation to all the human dead.',
                accent: 'amber',
                badges: ['Evangelical', 'Exegetical', 'Moderate'],
                summary: 'This interpretation focuses on 1 Peter 3:18–22 and links the “spirits in prison” with the rebellious beings associated with Noah’s generation. Christ’s proclamation is an announcement of victory and subjection, not necessarily evangelism. Some versions place the proclamation in the interval before resurrection; others understand it through the risen Christ’s triumph.',
                contribution: 'It reads 1 Peter within its themes of hostile powers, baptism, and Christ’s exaltation while avoiding the conclusion that the passage establishes a general postmortem opportunity for salvation.',
                caution: 'The view depends on disputed identifications of the spirits, the timing of the proclamation, and the relationship between 1 Peter, Genesis 6, and later Jewish traditions.',
                supporters: [{ name: 'Wayne A. Grudem', sourceId: 'grudem-first-peter' }],
                predicates: [
                    {
                        label: 'The gospel does not promise a general saving opportunity after death',
                        topicSlug: 'what-happens-to-people-who-never-hear-the-gospel',
                        readingId: 'explicit-faith-required',
                        linked: true,
                    },
                    {
                        label: 'Christ’s resurrection publicly defeats hostile spiritual powers',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'qualifies', target: 'victorious-descent', linked: true },
                    { type: 'contrasts with', target: 'state-of-death', linked: true },
                ],
            },
            {
                id: 'state-of-death',
                title: 'Christ remained among the dead',
                thesis: 'The creedal descent expresses Christ’s real burial and continuation in the state of the dead until resurrection, not a distinct journey to preach in hell.',
                accent: 'navy',
                badges: ['Reformed', 'Confessional', 'Conservative'],
                summary: 'The Westminster tradition explains the descent language through Christ’s humiliation after death: he was buried and continued under the power of death until the third day. This safeguards the reality of his death while reading difficult descent texts without constructing an additional ministry in the underworld. His saving work is complete at the cross and vindicated in resurrection.',
                contribution: 'It gives a restrained account closely tied to burial, resists speculative geography of the afterlife, and protects the completeness of Christ’s atoning work.',
                caution: 'This view must explain why the creed uses descent language beyond “buried” and offer persuasive readings of passages that appear to describe proclamation or descent to lower regions.',
                supporters: [{ name: 'Westminster Larger Catechism', sourceId: 'westminster-humiliation' }],
                predicates: [
                    {
                        label: 'The cross completes Christ’s atoning achievement before resurrection',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        readingId: 'forensic-verdict',
                        linked: true,
                    },
                    {
                        label: 'Christian hope rests on bodily resurrection rather than underworld speculation',
                        topicSlug: 'what-happens-after-death',
                        readingId: 'resurrection-and-new-creation',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'victorious-descent', linked: true },
                    { type: 'contrasts with', target: 'proclamation-to-spirits', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'catechism-descent',
                'Catechism of the Catholic Church',
                'Christ Descended Into Hell',
                'Official Catholic teaching on Christ’s descent to the realm of the dead as the victorious Saviour.',
                'https://www.vatican.va/content/catechism/en/part_one/section_two/chapter_two/article_5/paragraph_1_christ_descended_into_hell.index.html',
            ),
            source(
                'oca-holy-saturday',
                'Orthodox Church in America',
                'Great and Holy Saturday',
                'An Orthodox liturgical account of Christ’s descent into Hades and victory over death.',
                'https://www.oca.org/saints/lives/1003/03/27/26-great-and-holy-saturday',
            ),
            source(
                'grudem-first-peter',
                'Wayne A. Grudem',
                '1 Peter: Tyndale New Testament Commentary',
                'An evangelical commentary defending a proclamation of victory to imprisoned spirits associated with Noah’s day.',
                'https://ivpress.com/1-peter-tntc',
            ),
            source(
                'westminster-humiliation',
                'Orthodox Presbyterian Church',
                'Westminster Larger Catechism, Question 50',
                'A Reformed explanation of the descent as burial and continuation in the state and power of death.',
                'https://www.opc.org/documents/MESV_col.html',
            ),
        ],
        generation,
    },
    {
        slug: 'what-does-it-mean-that-god-is-trinity',
        category: 'God and Christian Doctrine',
        question: 'What does it mean that God is Trinity?',
        shortTitle: 'What does the Trinity mean?',
        summary: 'Historic Christianity confesses one God in three really distinct persons: Father, Son, and Holy Spirit. The main Christian models do not dispute that creedal centre, but emphasise different ways of expressing divine unity, personal distinction, eternal relations, and the inseparable work of the three persons.',
        introduction: 'The doctrine gathers biblical claims that God is one, that Father, Son, and Spirit are each divine, and that they relate personally to one another. “Person” does not mean three separate beings, and “one God” does not mean one person wearing three masks. The perspectives below compare classical Western, Eastern relational, and social-analytic emphases while noting where each must guard the common confession.',
        seoDescription: 'Understand the Trinity through classical Western, Eastern Orthodox, and social-analytic models of one God in three persons.',
        lastModified: '2026-08-19',
        keyTexts: [
            { label: 'Matthew 28:16–20', href: '/read/Matt.28/#Matt.28.16-Matt.28.20' },
            { label: 'John 1:1–18', href: '/read/John.1/#John.1.1-John.1.18' },
            { label: 'John 14:15–31', href: '/read/John.14/#John.14.15-John.14.31' },
            { label: '2 Corinthians 13:11–14', href: '/read/2Cor.13/#2Cor.13.11-2Cor.13.14' },
        ],
        readings: [
            {
                id: 'classical-relational-unity',
                title: 'One God exists in three persons',
                thesis: 'The Father, Son, and Spirit are one indivisible divine being, distinguished eternally by relations of origin rather than by separate wills or powers.',
                accent: 'navy',
                badges: ['Classical theism', 'Catholic', 'Western', 'Pro-Nicene'],
                summary: 'Classical Western theology begins with the simplicity and unity of God. Each person is wholly and equally God, not one-third of God, and the persons are distinguished by relation: the Father begets, the Son is begotten, and the Spirit proceeds. Because the divine nature and action are one, creation and redemption are works of the undivided Trinity, though Scripture fittingly attributes missions to particular persons.',
                contribution: 'It strongly protects monotheism, equality, and inseparable divine action while grounding personal distinction in eternal relations rather than temporary roles.',
                caution: 'The model must explain the vivid interpersonal language of Scripture without reducing the persons to abstract relations or making their distinction difficult for ordinary readers to grasp.',
                supporters: [{ name: 'Catechism of the Catholic Church', sourceId: 'catechism-trinity' }],
                predicates: [
                    {
                        label: 'Holiness belongs first to God’s unique and indivisible identity',
                        topicSlug: 'what-is-holiness',
                        readingId: 'gods-holy-identity',
                        linked: true,
                    },
                    {
                        label: 'Sanctification draws believers into communion with God’s life',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'participation-in-divine-life',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'eastern-monarchy-communion', linked: true },
                    { type: 'qualifies', target: 'social-trinitarian-model', linked: true },
                ],
            },
            {
                id: 'eastern-monarchy-communion',
                title: 'The Father is the source within the Trinity',
                thesis: 'The one God is eternally Father, Son, and Spirit, with the Father as personal source while the three remain one in essence and undivided in action.',
                accent: 'teal',
                badges: ['Orthodox', 'Eastern', 'Patristic', 'Pro-Nicene'],
                summary: 'Eastern Orthodox theology often begins with the concrete persons named in the creed and the monarchy of the Father: the Son is eternally begotten of the Father and the Spirit eternally proceeds from the Father. This does not make the Son or Spirit lesser; all three share one divine essence, glory, and action. Salvation is communion with the Father through the Son in the Holy Spirit.',
                contribution: 'It foregrounds the biblical and creedal relations among Father, Son, and Spirit and connects Trinitarian doctrine directly to worship, prayer, and participation in divine life.',
                caution: 'This emphasis must communicate divine unity without sounding as though the Father is a prior deity or source of a lesser divinity, and it must clarify historic East–West disagreements about the Spirit’s procession.',
                supporters: [{ name: 'Orthodox Church in America', sourceId: 'oca-trinity' }],
                predicates: [
                    {
                        label: 'Sanctification is participation in divine communion',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'participation-in-divine-life',
                        linked: true,
                    },
                    {
                        label: 'The church’s holiness is derived from belonging to the holy God',
                        topicSlug: 'what-is-holiness',
                        readingId: 'consecrated-belonging',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'classical-relational-unity', linked: true },
                    { type: 'qualifies', target: 'social-trinitarian-model', linked: true },
                ],
            },
            {
                id: 'social-trinitarian-model',
                title: 'The three persons share perfect communion',
                thesis: 'Father, Son, and Spirit are distinct personal centres whose necessary unity of divine nature, love, knowledge, and action constitutes the one God.',
                accent: 'amber',
                badges: ['Social Trinitarian', 'Analytic theology', 'Contemporary'],
                summary: 'Social models seek to take the “I–you” relations of Scripture with full seriousness by describing three distinct personal agents or centres of consciousness. Their unity is not merely cooperative agreement but perfect, necessary communion in the one divine nature. Different versions explain this unity through shared essence, powers, will, or mutual indwelling.',
                contribution: 'It makes the interpersonal language of Father, Son, and Spirit readily intelligible and highlights eternal love and communion rather than treating relational language as only an economic appearance.',
                caution: 'The model must avoid tritheism and show that perfect cooperation among three agents amounts to one God rather than a society of three divine beings. Historical labels such as “Eastern” and “social” must also not be treated as simple equivalents.',
                supporters: [
                    { name: 'William Hasker', sourceId: 'sep-trinity-models' },
                    { name: 'Richard Swinburne', sourceId: 'sep-trinity-models' },
                ],
                predicates: [
                    {
                        label: 'Divine holiness includes perfect personal love and communion',
                        topicSlug: 'what-is-holiness',
                        readingId: 'christlike-spirit-formed-life',
                        linked: true,
                    },
                    {
                        label: 'Sanctification forms a community in the pattern of divine love',
                        topicSlug: 'what-is-sanctification',
                        readingId: 'perfect-love',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'classical-relational-unity', linked: true },
                    { type: 'overlaps with', target: 'eastern-monarchy-communion', linked: true },
                ],
            },
        ],
        sources: [
            source(
                'catechism-trinity',
                'Catechism of the Catholic Church',
                'The Father: The Dogma of the Holy Trinity',
                'A classical account of one divine substance, three distinct persons, relations of origin, and inseparable action.',
                'https://www.vatican.va/content/catechism/en/part_one/section_two/chapter_one/article_1/paragraph_2_the_father.html',
            ),
            source(
                'oca-trinity',
                'Orthodox Church in America',
                'The Holy Trinity',
                'An Orthodox presentation of the biblical and creedal confession of one God, three distinct divine persons, and the Father as source.',
                'https://www.oca.org/cdn/PDFs/2023-0609-EOCB.pdf',
            ),
            source(
                'sep-trinity-models',
                'Stanford Encyclopedia of Philosophy',
                'Philosophy and Christian Theology: Trinity',
                'An academic overview of classical, social, and hybrid philosophical models of Trinitarian unity and distinction.',
                'https://plato.stanford.edu/entries/christiantheology-philosophy/',
            ),
        ],
        generation,
    },
];
